import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import { ChartTooltipV2, ChartTooltipV2Wrapper } from "@/components/ui/chartV2";
import { ChartDataPoint, ReferenceLineInfo, TickCallback } from "@/types/chart";
import { useChartDimensionsV2 } from "@/hooks/useChartDimensions";
import LegendWrapperV2 from "./components/legendWrapperV2";
import { formatValueV2 } from "@/common/valueFormatter";
import EventBus from "@/lib/eventBus";
import type { HandleLegendItemChangeArguments } from "@/types/report";
import { SeriesConfig } from "@/common/type/report-types";
import {
  ReportChartSettingsV2,
  ReportExtras,
} from "@/common/report-configuration";
import { useCurrentWorkspace } from "@/store/authContext/authContext";
import { resolveCategorySeriesColor } from "@/common/colorUtils";
import ChartAnnotations from "./components/chartAnnotations";

interface BarChartComponentV2Props {
  data: ChartDataPoint[];
  xAxis: string;
  xAxisLabel: string; // label for the x axis
  yAxisLeftLabel: string; // label for the left y axis
  yAxisRightLabel: string; // label for the right y axis
  seriesConfig: SeriesConfig;
  chartSettings: ReportChartSettingsV2;
  height?: number;
  maxHeight?: number;
  handleLegendItemChange: (args: HandleLegendItemChangeArguments) => void;
  stacked?: boolean;
  referenceLines?: ReferenceLineInfo[];
  reportExtras: ReportExtras;
}

const BarChartComponentV2: React.FC<BarChartComponentV2Props> = ({
  data,
  xAxis,
  xAxisLabel,
  yAxisLeftLabel,
  yAxisRightLabel,
  seriesConfig,
  chartSettings,
  handleLegendItemChange,
  stacked = false,
  maxHeight,
  referenceLines = [],
  reportExtras,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);

  // Measure container width on mount and resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const dimensions = useChartDimensionsV2(
    chartSettings,
    maxHeight,
    containerWidth,
  );

  const currentWorkspace = useCurrentWorkspace();

  const axisGroups = useMemo(() => {
    const groups = new Set<string>();
    Array.from(seriesConfig.values()).forEach((item) => {
      if (item.axisId) groups.add(item.axisId);
    });

    const orderedGroups = Array.from(groups);
    if (orderedGroups.length > 2) {
      throw new Error("BarChartComponentV2 supports up to 2 Y axes");
    }

    return orderedGroups;
  }, [seriesConfig]);

  const hasRightAxis = axisGroups.length === 2;

  const magnitude = useCallback((num: number) => {
    const absNum = Math.abs(num);
    if (absNum < 1) return 0;
    return Math.trunc(absNum).toString().length - 1;
  }, []);

  const calculateAxisGroupDomain = useCallback(
    (axisId: string): [number, number] => {
      let min = 0;
      let max = 0;

      const axisSeries = Array.from(seriesConfig.values()).filter(
        (item) => item.axisId === axisId && !item.hidden,
      );

      const seriesToUse =
        axisSeries.length > 0
          ? axisSeries
          : Array.from(seriesConfig.values()).filter(
              (item) => item.axisId === axisId,
            );

      seriesToUse.forEach((item) => {
        data.forEach((row) => {
          const value = row[item.key];
          if (typeof value === "number") {
            if (value < min) min = value;
            if (value > max) max = value;
          }
        });
      });

      if (max === 0 && min === 0) {
        return [0, 0];
      }

      const mag = Math.max(magnitude(max), magnitude(min));
      const base = Math.pow(10, mag);

      return [Math.floor(min / base) * base, Math.ceil(max / base) * base];
    },
    [seriesConfig, data, magnitude],
  );

  const domains = useMemo(() => {
    const leftAxisDomain = axisGroups[0]
      ? calculateAxisGroupDomain(axisGroups[0])
      : ([0, 0] as [number, number]);
    const rightAxisDomain = axisGroups[1]
      ? calculateAxisGroupDomain(axisGroups[1])
      : ([0, 0] as [number, number]);

    if (hasRightAxis) {
      const leftMax = leftAxisDomain[1];
      const rightMax = rightAxisDomain[1];

      if (
        (leftMax > rightMax && leftMax / rightMax <= 2) ||
        (rightMax > leftMax && rightMax / leftMax <= 2)
      ) {
        const unifiedMax = Math.max(leftMax, rightMax);
        leftAxisDomain[1] = unifiedMax;
        rightAxisDomain[1] = unifiedMax;
      }
    }

    return {
      leftAxisDomain,
      rightAxisDomain,
    };
  }, [axisGroups, calculateAxisGroupDomain, hasRightAxis]);

  const getAxisMeasure = useCallback(
    (axisId?: string) => {
      if (!axisId) return undefined;

      const visibleSeries = Array.from(seriesConfig.values()).find(
        (item) => item.axisId === axisId && !item.hidden,
      );
      if (visibleSeries) return visibleSeries.measure;

      return Array.from(seriesConfig.values()).find(
        (item) => item.axisId === axisId,
      )?.measure;
    },
    [seriesConfig],
  );

  const CustomXAxisTick = useCallback(
    ({ x, y, payload, index }: TickCallback) => {
      const CHAR_LIMIT = 14;
      const label = String(payload.value);
      const isLongLabel = label.length > CHAR_LIMIT;
      const displayLabel = isLongLabel
        ? `${label.substring(0, CHAR_LIMIT)}...`
        : label;

      // rawXValue is only present for time-dimension x-axes
      const rawXValue =
        typeof index === "number" ? data[index]?.rawXValue : undefined;
      const clicker = rawXValue
        ? () => EventBus.$emit("report:xAxisLabelClick", String(rawXValue))
        : undefined;

      return (
        <g transform={`translate(${x},${y})`} onClick={clicker}>
          <text
            x={0}
            y={0}
            dy={10}
            dx={0}
            textAnchor="end"
            transform="rotate(-45)"
            style={{ cursor: "pointer", fontSize: 13, fill: "#555" }}
          >
            {isLongLabel && <title>{label}</title>}
            {displayLabel}
          </text>
        </g>
      );
    },
    [data],
  );

  const renderXAxis = useCallback(() => {
    const axisProps = {
      dataKey: xAxis,
      angle: -90,
      textAnchor: "end" as const,
      dy: 10,
      dx: 0,
      tick: CustomXAxisTick,
    };

    const labelProps = {
      value: xAxisLabel,
      position: "bottom" as const,
      angle: 0,
      offset: 60,
      fontSize: 14,
      fontWeight: "bold",
    };

    return <XAxis {...axisProps} label={labelProps} />;
  }, [xAxisLabel, xAxis, CustomXAxisTick]);

  const renderYAxis = useCallback(() => {
    const leftAxisId = axisGroups[0];
    const rightAxisId = axisGroups[1];
    const leftMeasure = getAxisMeasure(leftAxisId);
    const rightMeasure = getAxisMeasure(rightAxisId);

    return (
      <>
        <YAxis
          key="left"
          yAxisId={leftAxisId}
          orientation="left"
          domain={domains.leftAxisDomain}
          tickFormatter={(value: number) =>
            leftMeasure
              ? formatValueV2(value, leftMeasure, currentWorkspace)
              : String(value)
          }
          label={{
            value: yAxisLeftLabel,
            position: "center",
            angle: -90,
            dx: -40,
            fontSize: 14,
            fontWeight: "bold",
          }}
        />
        {hasRightAxis && (
          <YAxis
            key="right"
            yAxisId={rightAxisId}
            orientation="right"
            domain={domains.rightAxisDomain}
            tickFormatter={(value: number) =>
              rightMeasure
                ? formatValueV2(value, rightMeasure, currentWorkspace)
                : String(value)
            }
            label={{
              value: yAxisRightLabel,
              position: "center",
              angle: 90,
              dx: 40,
              fontSize: 14,
              fontWeight: "bold",
            }}
          />
        )}
      </>
    );
  }, [
    axisGroups,
    currentWorkspace,
    domains.leftAxisDomain,
    domains.rightAxisDomain,
    getAxisMeasure,
    hasRightAxis,
    yAxisLeftLabel,
    yAxisRightLabel,
  ]);

  const bars = useMemo(() => {
    const visibleSeriesCount = Array.from(seriesConfig.values()).filter(
      (item) => !item.hidden,
    ).length;

    return Array.from(seriesConfig)
      .filter(([, item]) => !item.hidden)
      .map(([key, item]) => {
        return (
          <Bar
            key={key}
            yAxisId={item.axisId}
            dataKey={key}
            fill={item.color}
            fillOpacity={item.isPreviousPeriod ? 0.55 : 1}
            name={item.label}
            stackId={stacked ? "stack" : undefined}
          >
            {data.map((_, index) => {
              const categoryValue = data[index]?.[xAxis];
              const semanticCellColor =
                visibleSeriesCount === 1 && typeof categoryValue === "string"
                  ? resolveCategorySeriesColor(categoryValue)
                  : undefined;

              return (
                <Cell
                  key={`cell-${key}-${index}`}
                  fill={semanticCellColor ?? item.color}
                />
              );
            })}
          </Bar>
        );
      });
  }, [seriesConfig, stacked, data, xAxis]);

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: ["top", "bottom"].includes(
          chartSettings.legendPosition || "right",
        )
          ? "column"
          : "row",
        alignItems: "center",
        width: "100%",
        height: "100%",
      }}
    >
      {chartSettings.includeLegend &&
        ["left", "top"].includes(chartSettings.legendPosition || "right") && (
          <LegendWrapperV2
            position={chartSettings.legendPosition || "right"}
            items={seriesConfig}
            onItemChange={handleLegendItemChange}
          />
        )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          height:
            chartSettings.legendPosition === "bottom"
              ? "calc(100% - 100px)"
              : "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ChartAnnotations reportExtras={reportExtras} />
        <BarChart
          data={data}
          width={dimensions.chartWidth}
          height={dimensions.chartHeight}
          margin={dimensions.margin}
          barGap="10%"
        >
          <CartesianGrid strokeDasharray="3 3" />
          {referenceLines &&
            referenceLines.map((refLine) => (
              <ReferenceLine
                key={refLine.label}
                yAxisId={refLine.yAxisId}
                y={refLine.y}
                stroke="#BBB"
                label={{
                  position: "top",
                  value: refLine.label,
                }}
              />
            ))}
          <ChartTooltipV2
            content={(props) => (
              <ChartTooltipV2Wrapper
                chartType="Bar"
                seriesConfig={seriesConfig}
                showCount={chartSettings.showCount}
                {...props}
              />
            )}
          />
          {bars}
          {renderXAxis()}
          {renderYAxis()}
        </BarChart>
      </div>

      {chartSettings.includeLegend &&
        ["right", "bottom"].includes(
          chartSettings.legendPosition || "right",
        ) && (
          <LegendWrapperV2
            position={chartSettings.legendPosition || "right"}
            items={seriesConfig}
            onItemChange={handleLegendItemChange}
          />
        )}
    </div>
  );
};

// Update memo export to work with forwardRef
export default React.memo(BarChartComponentV2) as typeof BarChartComponentV2;
